<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { useMeta } from "@/composables/use-meta";
import axios from 'axios';
import { BASE_URL, BASE_URL_3 } from '@/Config/config';
import { useStore } from 'vuex';

useMeta({ title: "Driver Violation Reports" });

const store = useStore();

// Search and filter variables
const searchValue = ref('');
const startDate = ref('');
const endDate = ref('');
const isLoading = ref(false);
const selectedViolationId = ref('');
const selectedCargoType = ref('');
const selectedDirection = ref('');

// Pagination variables
const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalPages = computed(() => Math.ceil(driverViolationMatrix.value.length / itemsPerPage.value));

// Data variables
const violations = ref([]);
const violationActions = ref([]);
const drivers = ref([]);
const trucks = ref([]);
const trailers = ref([]);
const loading_order_item = ref([]);

// Computed properties
const filteredActions = computed(() => {
    let filtered = violationActions.value;

    // Filter by date range
    if (startDate.value && endDate.value) {
        filtered = filtered.filter(action => {
            const actionDate = action.created_at ? new Date(action.created_at) : null;
            if (!actionDate) return false;
            return (
                actionDate >= new Date(startDate.value) &&
                actionDate <= new Date(endDate.value + 'T23:59:59')
            );
        });
    }

    // Filter by search term
    if (searchValue.value) {
        const keyword = searchValue.value.toLowerCase();
        filtered = filtered.filter(action => {
            const driver = drivers.value.find(d => d.employee_id == action.employee_id);
            const truck = trucks.value.find(t => t.id == action.truck_id);
            const trailer = trailers.value.find(t => t.id == action.trailer_id);

            return (
                (driver?.driver || '').toLowerCase().includes(keyword) ||
                (truck?.registration_number || '').toLowerCase().includes(keyword) ||
                (trailer?.registration_number || '').toLowerCase().includes(keyword)
            );
        });
    }

    // Filter by cargo type
    if (selectedCargoType.value) {
        filtered = filtered.filter(action => {
            const loadingOrder = loading_order_item.value.find(item => item.id == action.loading_order_item_id);
            return loadingOrder?.cargo_type === selectedCargoType.value;
        });
    }

    // Filter by direction
    if (selectedDirection.value) {
        filtered = filtered.filter(action => {
            const loadingOrder = loading_order_item.value.find(item => item.id == action.loading_order_item_id);
            return loadingOrder?.type === selectedDirection.value;
        });
    }

    return filtered;
});

const driverViolationMatrix = computed(() => {
    const driverMap = new Map();

    // Initialize all drivers with zero counts for all violations
    drivers.value.forEach(driver => {
        const violationCounts = {};
        violations.value.forEach(violation => {
            violationCounts[violation.id] = 0;
        });

        driverMap.set(driver.employee_id, {
            driver: driver.driver,
            employee_id: driver.employee_id,
            totalCount: 0,
            violationCounts: violationCounts,
            truckIds: new Set(),
            trailerIds: new Set()
        });
    });

    // Count violations for each driver
    filteredActions.value.forEach(action => {
        if (!driverMap.has(action.employee_id)) {
            // If driver not in our initial list, add them
            const violationCounts = {};
            violations.value.forEach(violation => {
                violationCounts[violation.id] = 0;
            });

            driverMap.set(action.employee_id, {
                driver: getDriverName(action.employee_id),
                employee_id: action.employee_id,
                totalCount: 0,
                violationCounts: violationCounts,
                truckIds: new Set(),
                trailerIds: new Set()
            });
        }

        const driverData = driverMap.get(action.employee_id);
        driverData.totalCount++;

        // Track unique trucks and trailers
        if (action.truck_id) driverData.truckIds.add(action.truck_id);
        if (action.trailer_id) driverData.trailerIds.add(action.trailer_id);

        // Count violations by type
        if (action.violation_id) {
            driverData.violationCounts[action.violation_id]++;
        }
    });

    // Convert to array, filter out drivers with no violations, and sort by totalCount descending
    return Array.from(driverMap.values())
        .filter(driver => driver.totalCount > 0)
        .sort((a, b) => b.totalCount - a.totalCount);
});

// Paginated data
const paginatedDrivers = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const end = start + itemsPerPage.value;
    return driverViolationMatrix.value.slice(start, end);
});

// Pagination methods
const goToPage = (page) => {
    if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
    }
};

const nextPage = () => {
    if (currentPage.value < totalPages.value) {
        currentPage.value++;
    }
};

const prevPage = () => {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
};

const firstPage = () => {
    currentPage.value = 1;
};

const lastPage = () => {
    currentPage.value = totalPages.value;
};

// Reset to first page when filters change
watch([searchValue, startDate, endDate, selectedCargoType, selectedDirection], () => {
    currentPage.value = 1;
});

// Helper functions
const getDriverName = (employeeId) => {
    const driver = drivers.value.find(d => d.employee_id == employeeId);
    return driver?.driver || 'N/A';
};

const getTruckNumber = (truckId) => {
    const truck = trucks.value.find(t => t.id == truckId);
    return truck?.registration_number || 'N/A';
};

const getTrailerNumber = (trailerId) => {
    const trailer = trailers.value.find(t => t.id == trailerId);
    return trailer?.registration_number || 'N/A';
};

const getMostUsedTruck = (truckIds) => {
    if (!truckIds || truckIds.size === 0) return 'N/A';
    return getTruckNumber(Array.from(truckIds)[0]);
};

const getMostUsedTrailer = (trailerIds) => {
    if (!trailerIds || trailerIds.size === 0) return 'N/A';
    return getTrailerNumber(Array.from(trailerIds)[0]);
};

const loadDrivers = async () => {
    try {
        const response = await axios.get(`${BASE_URL_3}/company/employees/list?company_id=2`);
        drivers.value = response.data.employees || response.data;
        console.log("Drivers loaded:", drivers.value);
    } catch (error) {
        console.error('Error fetching employees:', error);
    }
};

const loadViolations = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/violations`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        violations.value = response.data.data || response.data;
        console.log("Violations loaded:", violations.value);
    } catch (error) {
        console.error('Error fetching violations:', error);
    }
};

// Load Violation Actions
const loadViolationActions = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/abs-violation-actions/index`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        const actions = response.data.data || response.data;

        violationActions.value = actions.map(action => {
            // Get numeric driver ID
            const driverId = typeof action.driver_id === 'string'
                ? parseInt(action.driver_id)
                : action.driver_id;

            // Find matching driver
            const driver = drivers.value.find(d => d.id === driverId);

            return {
                ...action,
                // Store both the numeric ID and the driver's actual name
                driver_id: driverId,
                driver_id: driver?.name || action.driver_id
            };
        });

        console.log("Mapped violation actions:", violationActions.value);
    } catch (error) {
        console.error('Error loading violation actions:', error);
    }
};

// Data loading
onMounted(async () => {
    isLoading.value = true;
    try {
        await Promise.all([
            loadDrivers(),
            loadViolations(),
            loadViolationActions(),
        ])

    } catch (err) {
        console.error("Failed to load data:", err);
    } finally {
        isLoading.value = false;
    }
});

const printReport = () => {
    const originalItemsPerPage = itemsPerPage.value;
    itemsPerPage.value = 10000; // Large number to show all items in print

    setTimeout(() => {
        const tableContent = document.querySelector('table.table-bordered')?.cloneNode(true);
        if (!tableContent) return;

        // Remove pagination controls from print
        ['.pagination', '.items-per-page-selector'].forEach(selector => {
            tableContent.querySelectorAll(selector).forEach(el => el.remove());
        });

        // Get the selected violation title
        const reportTitle = 'Driver Violation Summary Report';

        // Get cargo type from filtered actions
        let cargoTypeHeader = '';
        const uniqueCargoTypes = new Set();

        // Collect all unique cargo types from filtered actions
        filteredActions.value.forEach(action => {
            if (action.loading_order_item_id) {
                const loadingOrderItem = loading_order_item.value.find(
                    item => item.id == action.loading_order_item_id
                );
                if (loadingOrderItem?.cargo_type) {
                    uniqueCargoTypes.add(loadingOrderItem.cargo_type);
                }
            }
        });

        // Format cargo type header
        if (uniqueCargoTypes.size > 0) {
            if (uniqueCargoTypes.size === 1) {
                cargoTypeHeader = `Fleet Type: ${Array.from(uniqueCargoTypes)[0]}`;
            } else {
                cargoTypeHeader = `Fleet Types: ${Array.from(uniqueCargoTypes).join(', ')}`;
            }
        }

        const printWindow = window.open('', '', 'width=800,height=900');
        if (!printWindow) return;

        // Date range text
        let dateRangeText = ''
        if (startDate.value && endDate.value) {
            dateRangeText = `From ${startDate.value} to ${endDate.value}`
        }

        printWindow.document.write(`
            <html>
                <head>
                    <title>${reportTitle}</title>
                    <style>
                        @page {
                            size: A4 landscape;
                            margin: 10pt;
                        }
                        body {
                            font-family: Arial, sans-serif;
                            font-size: 7px;
                            padding: 15px;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                        .header {
                            text-align: center;
                            margin-bottom: 20px;
                        }
                        .header .title {
                            font-size: 24px;
                            font-weight: bold;
                            color: red;
                        }
                        .sub-header {
                            display: flex;
                            justify-content: space-between;
                            margin-bottom: 10px;
                        }
                        .report-title {
                            font-size: 18px;
                            font-weight: bold;
                            text-decoration: underline;
                        }
                        .fleet-type {
                            font-size: 14px;
                            font-weight: bold;
                        }
                        .date-range { font-weight: bold; margin-bottom: 10px; font-size: 14px; }
                        table {
                            width: 100%;
                            border-collapse: collapse;
                            margin-top: 20px;
                        }
                        th, td {
                            border: 1px solid #000 !important;
                            padding: 8px;
                            text-align: center;
                        }
                        td {
                            font-size: 7px;
                        }
                        th {
                            background-color: #f8f9fa !important;
                            font-weight: bold;
                        }
                        .footer {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            margin-top: 40px;
                            border-top: 2px solid #000;
                            padding-top: 10px;
                            font-size: 10px;
                            flex-wrap: wrap;
                            gap: 10px;
                        }
                        .footer > div {
                            min-width: 18%;
                        }
                        .print-actions {
                            display: flex;
                            justify-content: center;
                            margin: 20px 0;
                            gap: 10px;
                        }
                        button {
                            padding: 8px 16px;
                            border: none;
                            border-radius: 4px;
                            cursor: pointer;
                            font-weight: bold;
                        }
                        .btn-print {
                            background-color: #4CAF50;
                            color: white;
                        }
                        .btn-pdf {
                            background-color: #2196F3;
                            color: white;
                        }
                        .btn-close {
                            background-color: #f44336;
                            color: white;
                        }
                        @media print {
                            .print-actions {
                                display: none !important;
                            }
                        }
                        tr {
                            page-break-inside: avoid;
                        }
                        .total-col {
                            font-weight: bold;
                            background-color: #f1f1f1;
                        }
                    </style>
                    <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"><\/script>
                </head>
                <body>
                    <div id="content-to-export">
                        <div class="header">
                            <div class="title">MA CARGO TRUCKERS & FORWADERS</div>
                            <div>P.O Box 678, Morogoro Tanzania</div>
                            <div>Plot No. 9-14 Block C Msamvu Industrial Area, Tanzania</div>
                            <div>Mob: +255 (0) 23 261 3589</div>
                            <div>Email: info@marcargo.co.tz</div>
                        </div>
                        <div class="sub-header">
                            <div class="report-title">${reportTitle}</div>
                            ${dateRangeText ? `<div class="date-range">${dateRangeText}</div>` : ''}
                            ${cargoTypeHeader ? `<div class="fleet-type">${cargoTypeHeader}</div>` : ''}
                        </div>
                        <div class="content">
                            ${tableContent.outerHTML}
                        </div>
                        <div class="footer">
                            <div>Prepared By: ____________________<br>Tracking Officer</div>
                            <div>____________________<br>Operation Officer</div>
                            <div>____________________<br>QHSE</div>
                            <div>_____________________<br>General Manager</div>
                            <div>_____________________<br>Managing Director</div>
                        </div>
                    </div>
                    <div class="print-actions">
                        <button onclick="window.print()" class="btn-print">Print Report</button>
                        <button onclick="downloadPDF()" class="btn-pdf">Download as PDF</button>
                        <button onclick="window.close()" class="btn-close">Close</button>
                    </div>
                    <script>
                        function downloadPDF() {
                            const element = document.getElementById('content-to-export');
                            const opt = {
                                margin: 10,
                                filename: 'driver_violation_report.pdf',
                                image: { type: 'jpeg', quality: 0.98 },
                                html2canvas: { 
                                    scale: 2,
                                    logging: true,
                                    useCORS: true
                                },
                                jsPDF: { 
                                    unit: 'mm', 
                                    format: 'a4', 
                                    orientation: 'landscape' 
                                }
                            };
                            
                            // Show loading indicator
                            const originalText = document.querySelector('.btn-pdf').textContent;
                            document.querySelector('.btn-pdf').textContent = 'Generating PDF...';
                            
                            // Generate PDF
                            html2pdf().set(opt).from(element).save().then(() => {
                                // Restore button text
                                document.querySelector('.btn-pdf').textContent = originalText;
                            });
                        }
                        
                        window.onload = function() {
                            // Focus the window for better UX
                            window.focus();
                        };
                    <\/script>
                </body>
            </html>
        `);

        printWindow.document.close();
        printWindow.focus();

        printWindow.onafterprint = () => {
            itemsPerPage.value = originalItemsPerPage;
        };
    }, 100);
};

const clearFilters = () => {
    startDate.value = '';
    endDate.value = '';
    searchValue.value = '';
    selectedCargoType.value = '';
    selectedDirection.value = '';
};
</script>

<template>
    <div class="layout-px-spacing">
        <teleport to="#breadcrumb">
            <ul class="navbar-nav flex-row">
                <li>
                    <div class="page-header">
                        <nav class="breadcrumb-one" aria-label="breadcrumb">
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item active" aria-current="page">
                                    <span>Driver Violation Reports</span>
                                </li>
                            </ol>
                        </nav>
                    </div>
                </li>
            </ul>
        </teleport>

        <div class="row layout-top-spacing">
            <div class="col-xl-12 col-lg-12 col-md-12 col-12 layout-spacing">
                <div class="panel br-4">
                    <div class="panel-body">
                        <h3 class="mb-0">Driver Violation Summary Report
                            <strong>
                                Showing {{ driverViolationMatrix.length }} drivers with violations
                            </strong>
                        </h3>

                        <!-- Filter controls -->
                        <div class="report-header mt-3">
                            <div class="filter-row row g-2 mb-2">
                                <div class="col-12 col-md-6 col-lg-4">
                                    <div class="d-flex align-items-center gap-2">
                                        <label>From:</label>
                                        <input type="date" v-model="startDate" class="form-control">
                                    </div>
                                </div>
                                <div class="col-12 col-md-6 col-lg-4">
                                    <div class="d-flex align-items-center gap-2">
                                        <label>To:</label>
                                        <input type="date" v-model="endDate" class="form-control">
                                    </div>
                                </div>
                                <div class="col-12 col-md-6 col-lg-4">
                                    <button class="btn btn-danger w-100" @click="clearFilters">
                                        Clear Filters
                                    </button>
                                </div>
                            </div>

                            <div class="filter-row row g-2 mb-3">
                                <div class="col-12 col-md-3">
                                    <input type="text" v-model="searchValue"
                                        placeholder="Search driver, truck, trailer..." class="form-control">
                                </div>
                                <div class="col-12 col-md-3 col-lg-3">
                                    <div class="d-flex align-items-center gap-2">
                                        <label>Cargo Type:</label>
                                        <select v-model="selectedCargoType" class="form-control">
                                            <option value="">All Cargo Types</option>
                                            <option value="LOOSE_CARGO">Loose Cargo</option>
                                            <option value="TANKERS">Tankers</option>
                                            <option value="CONTAINER">Container</option>
                                        </select>
                                    </div>
                                </div>
                                <div class="col-12 col-md-3 col-lg-3">
                                    <div class="d-flex align-items-center gap-2">
                                        <label>Direction:</label>
                                        <select v-model="selectedDirection" class="form-control">
                                            <option value="">All Directions</option>
                                            <option value="GO">GO</option>
                                            <option value="RETURN">RETURN</option>
                                        </select>
                                    </div>
                                </div>
                                <div class="col-12 col-md-3">
                                    <button
                                        class="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
                                        @click="printReport">
                                        Print Report
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                            stroke-linecap="round" stroke-linejoin="round"
                                            class="feather feather-printer">
                                            <polyline points="6 9 6 2 18 2 18 9"></polyline>
                                            <path
                                                d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2">
                                            </path>
                                            <rect x="6" y="14" width="12" height="8"></rect>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Items per page selector -->
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <div class="d-flex align-items-center">
                                <span class="me-2">Show:</span>
                                <select v-model="itemsPerPage" class="form-select form-select-sm" style="width: 80px;">
                                    <option value="5">5</option>
                                    <option value="10">10</option>
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                                <span class="ms-2">entries</span>
                            </div>
                            <div class="text-muted">
                                Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
                                {{ Math.min(currentPage * itemsPerPage, driverViolationMatrix.length) }} of
                                {{ driverViolationMatrix.length }} entries
                            </div>
                        </div>

                        <!-- Loading state -->
                        <div v-if="isLoading" class="text-center p-4">
                            <div class="spinner-border text-primary" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <p class="mt-2">Loading reports...</p>
                        </div>

                        <!-- Data table -->
                        <div v-else class="table-responsive">
                            <table class="table table-hover table-bordered">
                                <thead>
                                    <tr>
                                        <th rowspan="2">S/N</th>
                                        <th rowspan="2">Driver</th>
                                        <th rowspan="2">Truck</th>
                                        <th rowspan="2">Trailer</th>
                                        <th :colspan="violations.length">Violation Counts</th>
                                        <th rowspan="2" class="total-col">Total</th>
                                    </tr>
                                    <tr>
                                        <th v-for="violation in violations" :key="violation.id">
                                            {{ violation.title }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(driver, index) in paginatedDrivers" :key="driver.employee_id">
                                        <td>{{ (currentPage - 1) * itemsPerPage + index + 1 }}</td>
                                        <td>{{ driver.driver }}</td>
                                        <td>{{ getMostUsedTruck(driver.truckIds) }}</td>
                                        <td>{{ getMostUsedTrailer(driver.trailerIds) }}</td>
                                        <td v-for="violation in violations" :key="violation.id">
                                            {{ driver.violationCounts[violation.id] || 0 }}
                                        </td>
                                        <td class="total-col">{{ driver.totalCount }}</td>
                                    </tr>
                                    <tr v-if="driverViolationMatrix.length === 0">
                                        <td :colspan="5 + violations.length" class="text-center text-muted">
                                            No drivers with violations found matching your criteria
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <!-- Pagination -->
                            <div class="paginating-container pagination-solid flex-column align-items-center mt-4">
                                <ul role="menubar" aria-disabled="false" aria-label="Pagination"
                                    class="pagination mb-4 b-pagination">
                                    <li role="presentation" class="page-item first"
                                        :class="{ disabled: currentPage === 1 }">
                                        <button role="menuitem" type="button" @click="firstPage"
                                            :disabled="currentPage === 1" :tabindex="currentPage === 1 ? -1 : 0"
                                            aria-label="Go to first page" class="page-link">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                                viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M11 19l-7-7 7-7m8 14l-7-7 7-7"></path>
                                            </svg>
                                        </button>
                                    </li>
                                    <li role="presentation" class="page-item prev"
                                        :class="{ disabled: currentPage === 1 }">
                                        <button role="menuitem" type="button" @click="prevPage"
                                            :disabled="currentPage === 1" :tabindex="currentPage === 1 ? -1 : 0"
                                            aria-label="Go to previous page" class="page-link">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                                viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M15 19l-7-7 7-7"></path>
                                            </svg>
                                        </button>
                                    </li>
                                    <li role="presentation" class="page-item" v-for="page in totalPages" :key="page"
                                        :class="{ active: currentPage === page }">
                                        <button role="menuitemradio" type="button" @click="goToPage(page)"
                                            :aria-label="`Go to page ${page}`" :aria-checked="currentPage === page"
                                            :aria-posinset="page" :aria-setsize="totalPages"
                                            :tabindex="currentPage === page ? 0 : -1" class="page-link">
                                            {{ page }}
                                        </button>
                                    </li>
                                    <li role="presentation" class="page-item next"
                                        :class="{ disabled: currentPage === totalPages }">
                                        <button role="menuitem" type="button" @click="nextPage"
                                            :disabled="currentPage === totalPages"
                                            :tabindex="currentPage === totalPages ? -1 : 0" aria-label="Go to next page"
                                            class="page-link">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                                viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M9 5l7 7-7 7"></path>
                                            </svg>
                                        </button>
                                    </li>
                                    <li role="presentation" class="page-item last"
                                        :class="{ disabled: currentPage === totalPages }">
                                        <button role="menuitem" type="button" @click="lastPage"
                                            :disabled="currentPage === totalPages"
                                            :tabindex="currentPage === totalPages ? -1 : 0" aria-label="Go to last page"
                                            class="page-link">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                                viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M13 5l7 7-7 7M5 5l7 7-7 7"></path>
                                            </svg>
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.layout-px-spacing {
    min-height: calc(100vh - 170px) !important;
}

.report-header {
    background-color: #f8f9fa;
    padding: 1rem;
    border-radius: 4px;
    margin-bottom: 1.5rem;
}

.filter-row {
    align-items: center;
}

.filter-row label {
    white-space: nowrap;
    margin-bottom: 0;
    font-weight: 500;
}

.text-success {
    color: #28a745;
}

.text-warning {
    color: #ffc107;
}

.text-danger {
    color: #dc3545;
}

.table-responsive {
    overflow-x: auto;
}

@media print {

    .report-header,
    .breadcrumb-one,
    .pagination {
        display: none !important;
    }
}

@media (max-width: 767.98px) {
    .filter-row>div {
        margin-bottom: 0.5rem;
    }
}

.total-col {
    font-weight: bold;
    background-color: #f1f1f1;
}

th {
    text-align: center;
    vertical-align: middle;
}

/* Pagination styles */
.pagination {
    display: flex;
    padding-left: 0;
    list-style: none;
    border-radius: 0.25rem;
}

.page-item {
    margin: 0 2px;
}

.page-item.disabled .page-link {
    color: #6c757d;
    pointer-events: none;
    background-color: #fff;
    border-color: #dee2e6;
}

.page-item.active .page-link {
    z-index: 3;
    color: #fff;
    background-color: #3b3f5c;
    border-color: #3b3f5c;
}

.page-link {
    position: relative;
    display: block;
    padding: 0.5rem 0.75rem;
    margin-left: -1px;
    line-height: 1.25;
    color: #3b3f5c;
    background-color: #fff;
    border: 1px solid #dee2e6;
    text-decoration: none;
}

.page-link:hover {
    z-index: 2;
    color: #1d1d1d;
    background-color: #e9ecef;
    border-color: #dee2e6;
}

.page-link:focus {
    z-index: 3;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(59, 63, 92, 0.25);
}
</style>